/**
 * MapPreviewPicker
 *
 * Shared bare-bones live map used to pick a center + zoom: a vis.gl `<Map>` with
 * a basemap style resolved from the active color scheme, a `MAP_CONFIG` fallback
 * center when none is set yet, and a draggable `AnchorMarker` over the center.
 *
 * It deliberately owns only that shared core — camera control (imperative
 * `flyTo`/`easeTo`, zoom controls, drag hints), attribution, padding, and zoom
 * bounds are passed in by each consumer so neither the share wizard's start-view
 * picker nor the workspace map configurator preview has to approximate the other.
 *
 * Overlays (zoom pill, drag hint, loading placeholder) are rendered through the
 * `children` slot as siblings of `<Map>` inside the positioned wrapper.
 *
 * @module components/map/MapPreviewPicker
 */
import { memo } from 'react';
import type { ReactNode, Ref } from 'react';
import { Map } from '@vis.gl/react-maplibre';
import type {
  MapProps,
  MapRef,
  ViewStateChangeEvent,
} from '@vis.gl/react-maplibre';
import { useComputedColorScheme } from '@mantine/core';

import { AnchorMarker } from '@/components/map/AnchorMarker';
import { MAP_CONFIG, resolveBasemapStyle } from '@/config/maplibre';
import type { BasemapId } from '@/config/maplibre';
import type { Coordinates } from '@/types';

/** Per-consumer `<Map>` options that differ between the two preview pickers. */
export interface MapPreviewPickerMapOptions {
  minZoom: number;
  maxZoom: number;
  scrollZoom?: boolean;
  doubleClickZoom?: boolean;
  dragPan?: boolean;
  dragRotate?: boolean;
  styleDiffing?: boolean;
  /** Passed straight to the vis.gl `<Map>` (e.g. `false` or `{ compact: true }`). */
  attributionControl?: MapProps['attributionControl'];
  padding?: MapProps['padding'];
}

interface MapPreviewPickerProps {
  /** Live map center (draggable), or `null` while unset. */
  center: Coordinates | null;
  /** Start zoom — seeds the initial view and the marker's responsive sizing. */
  zoom: number;
  /** Basemap whose style the preview renders. */
  basemapId: BasemapId;
  /** Called when the marker is dragged to a new center. */
  onCenterChange: (coords: Coordinates) => void;
  /** Forwarded to `<Map ref>` so consumers can drive the camera imperatively. */
  mapRef?: Ref<MapRef>;
  /** Map load handler. */
  onLoad?: () => void;
  /** Move-end handler (e.g. to capture the picked zoom). */
  onMoveEnd?: (event: ViewStateChangeEvent) => void;
  /**
   * Whether the anchor marker is rendered. Defaults to "center is set"; pass an
   * explicit value to also gate on map readiness.
   */
  markerVisible?: boolean;
  /** Consumer-owned `<Map>` options (zoom bounds, attribution, padding, …). */
  mapOptions: MapPreviewPickerMapOptions;
  /** Wrapper class — owns the preview's size / border / positioning. */
  className?: string;
  /** Overlays rendered as siblings of `<Map>` (zoom pill, hint, placeholder). */
  children?: ReactNode;
}

export const MapPreviewPicker = memo(function MapPreviewPicker({
  center,
  zoom,
  basemapId,
  onCenterChange,
  mapRef,
  onLoad,
  onMoveEnd,
  markerVisible,
  mapOptions,
  className,
  children,
}: MapPreviewPickerProps) {
  const colorScheme = useComputedColorScheme('light');

  const mapStyle = resolveBasemapStyle(basemapId, colorScheme);

  // Mount-only initial camera; live updates flow via the marker / consumer.
  const initialViewState = {
    longitude: center?.lng ?? MAP_CONFIG.center[0],
    latitude: center?.lat ?? MAP_CONFIG.center[1],
    zoom,
  };

  const showMarker = markerVisible ?? center !== null;

  return (
    <div className={className}>
      <Map
        ref={mapRef}
        initialViewState={initialViewState}
        mapStyle={mapStyle}
        onLoad={onLoad}
        onMoveEnd={onMoveEnd}
        minZoom={mapOptions.minZoom}
        maxZoom={mapOptions.maxZoom}
        scrollZoom={mapOptions.scrollZoom}
        doubleClickZoom={mapOptions.doubleClickZoom}
        dragPan={mapOptions.dragPan}
        dragRotate={mapOptions.dragRotate}
        styleDiffing={mapOptions.styleDiffing}
        attributionControl={mapOptions.attributionControl}
        padding={mapOptions.padding}
      >
        {showMarker && center && (
          <AnchorMarker
            coordinates={center}
            zoom={zoom}
            onDragEnd={onCenterChange}
          />
        )}
      </Map>

      {children}
    </div>
  );
});
